import type { FieldErrors } from "@octopusdeploy/octopus-react-client";
import type { Repository } from "@octopusdeploy/octopus-server-client";
import type { JwtPayload } from "jwt-decode";
import { jwtDecode } from "jwt-decode";
import moment from "moment";
import URI from "urijs";
import type { ArgoCDInstanceConfiguration } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDInstanceConfiguration";
import { ConfigurationMessages } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/ConfigurationMessages";
import { getExpiry } from "~/areas/infrastructure/components/ArgoCDInstances/useBearerToken";
import stringHelper from "~/utils/StringHelper/index";
export async function validateConfiguration(repo: Repository, modelToValidate: ArgoCDInstanceConfiguration): Promise<FieldErrors> {
    return {
        ...(await validateField("name", () => validateName(repo, modelToValidate.name))),
        ...(await validateField("environmentIds", () => validateEnvironments(modelToValidate.environmentIds))),
        ...(await validateField("argoCDUrl", () => validateArgoCDUrl(modelToValidate.argoCDUrl))),
        ...(await validateField("argoCDAuthenticationToken", () => validateArgoCDAuthToken(modelToValidate.argoCDAuthenticationToken))),
        ...(await validateField("octopusServerUrl", () => validateOctopusServerUrl(modelToValidate.octopusServerUrl))),
        ...(await validateField("octopusGrpcUrl", () => validateOctopusServerGrpcUrl(modelToValidate.octopusGrpcUrl))),
    };
}
async function validateField(field: keyof ArgoCDInstanceConfiguration, validateAction: () => string | undefined | Promise<string | undefined>): Promise<Partial<Record<keyof ArgoCDInstanceConfiguration, string>>> {
    const error = await validateAction();
    return error
        ? {
            [field]: error,
        }
        : {};
}
async function validateName(repo: Repository, name: string): Promise<string | undefined> {
    if (stringHelper.isNullOrWhiteSpace(name)) {
        return ConfigurationMessages.Name.Validation.NotProvided;
    }
    else {
        try {
            const existingGateways = await repo.ArgoCDInstances.list({ name });
            if (existingGateways.Resources.length > 0) {
                return ConfigurationMessages.Name.Validation.AlreadyExists;
            }
        }
        catch {
            return ConfigurationMessages.Name.Validation.UnableToValidate;
        }
    }
}
function validateEnvironments(environments: string[]): string | undefined {
    if (environments.length === 0) {
        return ConfigurationMessages.Environments.Validation.NotProvided;
    }
}
function validateArgoCDUrl(url: string): string | undefined {
    if (stringHelper.isNullOrWhiteSpace(url)) {
        return ConfigurationMessages.ArgoCDUri.Validation.NotProvided;
    }
}
function validateArgoCDAuthToken(token: string): string | undefined {
    if (stringHelper.isNullOrWhiteSpace(token)) {
        return ConfigurationMessages.ArgoCDAuthToken.Validation.NotProvided;
    }
    //try and decode the token into a valid JWT, if it fails, fail validation
    try {
        const jwt = jwtDecode<JwtPayload>(token);
        const expiry = getExpiry(jwt);
        //if there is an expiry, and it's less than now, we must have an expired token
        if (expiry && expiry < moment()) {
            return ConfigurationMessages.ArgoCDAuthToken.Validation.Expired;
        }
    }
    catch {
        return ConfigurationMessages.ArgoCDAuthToken.Validation.Invalid;
    }
}
function validateOctopusServerUrl(url: string): string | undefined {
    if (stringHelper.isNullOrWhiteSpace(url)) {
        return ConfigurationMessages.ServerUri.Validation.NotProvided;
    }
    else if (!isValidUrl(url)) {
        return ConfigurationMessages.ServerUri.Validation.Invalid;
    }
}
function validateOctopusServerGrpcUrl(url: string): string | undefined {
    if (stringHelper.isNullOrWhiteSpace(url)) {
        return ConfigurationMessages.ServerGrpcUri.Validation.NotProvided;
    }
    else if (!isValidUrl(url)) {
        return ConfigurationMessages.ServerGrpcUri.Validation.Invalid;
    }
}
function isValidUrl(url: string): boolean {
    try {
        const uri = new URI(url);
        return uri.is("absolute") && uri.is("url") && (uri.is("name") || uri.is("ip"));
    }
    catch (error) {
        return false;
    }
}
